import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Typography, Alert, Row, Col, Carousel, Spin } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { authAPI, slideshowAPI } from '../utils/api';
import { useSettings } from '../contexts/SettingsContext';

const { Title, Paragraph } = Typography;

interface LoginProps {
  onLogin: (token: string) => void;
}

interface LoginFormData {
  email: string;
  password: string;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [slides, setSlides] = useState<string[]>([]);
  const [slidesLoading, setSlidesLoading] = useState<boolean>(true);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);

  // Use settings context
  const { settings, loading: settingsLoading } = useSettings();

  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const response = await slideshowAPI.getSlides();
        setSlides(response.data.slides || []);
      } catch (error) {
        console.error('Failed to fetch slides:', error);
        setSlides([]);
      } finally {
        setSlidesLoading(false);
      }
    };

    fetchSlides();
  }, []);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const onFinish = async (values: LoginFormData): Promise<void> => {
    setLoading(true);
    setErrorMessage('');
    try {
      const response = await authAPI.login({
        email: values.email, // API still expects email field
        password: values.password,
        panel: 'vpnpanel'
      });

      const { token } = response.data;
      message.success('Login successful!');
      onLogin(token);
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo: any): void => {
    console.log('Failed:', errorInfo);
  };

  // Helper function to construct logo URL
  const getLogoUrl = (logoPath: string) => {
    return `${process.env.REACT_APP_API_URL}/file/public/view?file=${logoPath}`;
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: '#f0f2f5',
      padding: '20px',
      position: 'relative'
    }}>
      {/* Top Logo for Desktop */}
      {!isMobile && settings?.logo_images && (
        <img
          src={getLogoUrl(settings.logo_images)}
          alt="Logo"
          style={{
            position: 'fixed',
            top: '32px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '200px',
            zIndex: 1000
          }}
        />
      )}
      <Card
        variant={"outlined"}
        className="login-layout"
        style={{
          width: '100%',
          maxWidth: 800,
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }}
      >
        {errorMessage && (
          <>
            <Alert
              description={errorMessage}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          </>
        )}

        <Row>
          <Col xs={0} md={12}>
            <div style={{
              height: '400px',
              borderRadius: '8px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {slidesLoading ? (
                <div style={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f5f5f5'
                }}>
                  <Spin size="large" />
                </div>
              ) : slides.length > 0 ? (
                <Carousel
                  autoplay
                  dots={true}
                  effect="fade"
                  style={{ height: '100%' }}
                >
                  {slides.map((slide, index) => (
                    <div key={index}>
                      <div style={{
                        height: '400px',
                        backgroundImage: `url(${process.env.REACT_APP_API_URL}/file/public/view?file=${slide})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                        position: 'relative'
                      }}>
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                          padding: '40px 20px 20px',
                          color: 'white',
                          textAlign: 'center'
                        }}>
                        </div>
                      </div>
                    </div>
                  ))}
                </Carousel>
              ) : (
                <div style={{
                  height: '100%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '24px',
                  fontWeight: 'bold'
                }}>
                  {settings?.com_name || 'Loading ...'}
                </div>
              )}
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ padding: '20px', textAlign: isMobile ? 'center' : 'left' }}>
              <Form
                layout="vertical"
                name="login"
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                autoComplete="off"
                hideRequiredMark={true}
              >
                {/* Mobile Logo */}
                {isMobile && settings?.logo_images && (
                  <img
                    src={getLogoUrl(settings.logo_images)}
                    alt="Logo"
                    style={{
                      width: '250px',
                      maxWidth: '100%',
                      marginBottom: '20px'
                    }}
                  />
                )}
                <Title level={4}>Log in</Title>
                <Paragraph style={{ marginBottom: 24 }}>
                  Welcome Back! Please enter your E-mail address / User Name and password
                </Paragraph>

                <Form.Item
                  name="email"
                  label="E-mail/User Name"
                  rules={[
                    { required: true, message: 'Please Enter your Email Address/User Name.' },
                    { min: 5, max: 320, message: 'Length should be 5 to 320' }
                  ]}
                >
                  <Input 
                    type="text"
                    autoComplete="off"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label="Password"
                  rules={[
                    { required: true, message: 'Please put your password' },
                    { min: 5, max: 20, message: 'Length should be 5 to 20' }
                  ]}
                >
                  <Input.Password
                    autoComplete="off"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    style={{ float: 'left' }}
                  >
                    {loading ? 'Loading' : 'Sign in'}
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Footer */}
      <div style={{
        display: 'none',
        position: 'fixed',
        bottom: '60px',
        left: '50%',
        transform: 'translateX(-50%)',
        textAlign: 'center',
        zIndex: 1000
      }}>
        <div style={{ marginBottom: '10px' }}>
          <a 
            href="https://kloudvpn.net/privacy-policy/" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ padding: '0 5px', color: '#1890ff', textDecoration: 'none' }}
          >
            Privacy
          </a>
          <a 
            href="https://kloudvpn.net/terms-condition/" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ padding: '0 5px', color: '#1890ff', textDecoration: 'none' }}
          >
            Terms Of Service
          </a>
          <a 
            href="https://kloudvpn.net/contact-us/" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ padding: '0 5px', color: '#1890ff', textDecoration: 'none' }}
          >
            Contact
          </a>
        </div>
      </div>

      {/* Copyright */}
      <div style={{
        display: 'none',
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        textAlign: 'center',
        fontSize: '12px',
        color: '#666',
        zIndex: 1000
      }}>
        <div style={{ marginBottom: '5px' }}>
          &copy;{new Date().getFullYear()} Powered by{' '}
          <a 
            href="https://www.kloudvpn.net" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#1890ff', textDecoration: 'none' }}
          >
            KloudVPN
          </a>
        </div>
        <div>
          A product of{' '}
          <a 
            href="https://www.klouder.org" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#1890ff', textDecoration: 'none' }}
          >
            Klouder LLC
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
